/**
 * Enhanced Conditional Logging System
 * Development-only logging controlled by localStorage flag
 * with performance timing utilities
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  showTimestamp: boolean;
  showSource: boolean;
  prefix?: string;
}

interface LogData {
  message: string;
  data?: any;
  timestamp?: Date;
  source?: string;
  level: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_COLORS = {
  debug: 'color: #9CA3AF',
  info: 'color: #3B82F6',
  warn: 'color: #F59E0B',
  error: 'color: #EF4444',
};

class Logger {
  private config: LoggerConfig;
  private timers: Map<string, number> = new Map();

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      enabled: this.isDebugEnabled(),
      level: 'debug',
      showTimestamp: true,
      showSource: true,
      prefix: '[Flow]',
      ...config,
    };

    // Listen for config changes in development
    if (process.env.NODE_ENV === 'development') {
      window.addEventListener('storage', (e) => {
        if (e.key === 'logger-config' || e.key === 'DEBUG') {
          this.config.enabled = this.isDebugEnabled();
        }
      });
    }
  }

  private isDebugEnabled(): boolean {
    if (process.env.NODE_ENV !== 'development') return false;
    if (typeof window !== 'undefined') {
      return localStorage.getItem('DEBUG') === 'true';
    }
    return process.env.DEBUG === 'true';
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') {
      return false;
    }
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private getSource(): string {
    const error = new Error();
    const stack = error.stack?.split('\n');
    if (stack && stack.length > 3) {
      const caller = stack[3];
      const match = caller.match(/at\s+(.+)\s+\((.+):(\d+):(\d+)\)/);
      if (match) {
        const [, func, file, line] = match;
        const fileName = file.split('/').pop() || file;
        return `${func} (${fileName}:${line})`;
      }
    }
    return 'unknown';
  }

  private formatMessage(data: LogData): string {
    const parts: string[] = [];

    if (this.config.showTimestamp) {
      const time = (data.timestamp || new Date()).toISOString().split('T')[1].split('.')[0];
      parts.push(`[${time}]`);
    }

    parts.push(`[${data.level.toUpperCase()}]`);

    if (this.config.prefix) {
      parts.push(this.config.prefix);
    }

    if (this.config.showSource && data.source) {
      parts.push(`[${data.source}]`);
    }

    parts.push(data.message);

    return parts.join(' ');
  }

  private log(level: LogLevel, message: string, data?: any) {
    if (!this.shouldLog(level)) return;

    const logData: LogData = {
      message,
      data,
      timestamp: new Date(),
      source: this.config.showSource ? this.getSource() : undefined,
      level,
    };

    const formattedMessage = this.formatMessage(logData);
    const logMethod =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;

    if (data !== undefined) {
      logMethod(`%c${formattedMessage}`, LOG_COLORS[level], data);
    } else {
      logMethod(`%c${formattedMessage}`, LOG_COLORS[level]);
    }
  }

  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  error(message: string, error?: any) {
    this.log('error', message, error);
  }

  // Performance timing utilities
  time(label: string) {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') return;

    this.timers.set(label, performance.now());
    this.debug(`Timer started: ${label}`);
  }

  timeEnd(label: string): number | undefined {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') return;

    const start = this.timers.get(label);
    if (start === undefined) {
      this.warn(`Timer not found: ${label}`);
      return;
    }

    const duration = performance.now() - start;
    this.timers.delete(label);

    const formatted =
      duration < 1
        ? `${(duration * 1000).toFixed(2)}μs`
        : duration < 1000
          ? `${duration.toFixed(2)}ms`
          : `${(duration / 1000).toFixed(2)}s`;

    this.info(`Timer ended: ${label} (${formatted})`);
    return duration;
  }

  // Group logging
  group(label: string) {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') return;
    console.group(label);
  }

  groupEnd() {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') return;
    console.groupEnd();
  }

  // Table logging for structured data
  table(data: any, columns?: string[]) {
    if (!this.config.enabled || process.env.NODE_ENV !== 'development') return;
    console.table(data, columns);
  }

  // Create a child logger with a specific prefix
  child(prefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: `${this.config.prefix} ${prefix}`,
    });
  }

  // Toggle debug mode
  static toggleDebug(): void {
    if (typeof window !== 'undefined') {
      const current = localStorage.getItem('DEBUG') === 'true';
      localStorage.setItem('DEBUG', String(!current));
      console.log(`Debug logging ${!current ? 'enabled' : 'disabled'}. Refresh the page to apply.`);
    }
  }

  // Check if debug is enabled
  static isDebugEnabled(): boolean {
    if (process.env.NODE_ENV !== 'development') return false;
    if (typeof window !== 'undefined') {
      return localStorage.getItem('DEBUG') === 'true';
    }
    return process.env.DEBUG === 'true';
  }
}

// Create singleton instance
export const logger = new Logger();

// Export class for creating custom loggers
export { Logger };

// Export type definitions
export type { LogData, LogLevel, LoggerConfig };

// Helper to create scoped loggers
export function createScopedLogger(scope: string) {
  return logger.child(scope);
}

// Development console helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).toggleDebug = Logger.toggleDebug;
  (window as any).logger = logger;

  console.log(
    '%c🔧 Logger available: toggleDebug(), logger.debug(), logger.info(), logger.warn(), logger.error()',
    'color: #3B82F6; font-weight: bold'
  );
}
